import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, History, Pencil, QrCode, Wrench } from 'lucide-react';
import { getAsset } from '../../api/assets';
import { printAssetLabel } from '../../lib/assetLabel';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { StatusBadge, Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { AssetActionModal, type AssetActionKind } from './AssetActionModal';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800">{value ?? '-'}</p>
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  RETURN: 'Returned',
  DECOMMISSION: 'Decommissioned',
  REASSIGN: 'Reassigned',
  CONDITION_CHANGE: 'Condition updated',
  SERVICE: 'Serviced',
  LOAN_CHECKOUT: 'Checked out on loan',
  LOAN_CHECKIN: 'Checked in from loan',
};

// Matches the backend's WARRANTY_WARNING_DAYS (AssetsService) - purely a
// display cue here, the actual alert emails are driven by that constant.
const WARRANTY_WARNING_DAYS = 30;

function warrantyBadge(warrantyEndDate: string | null) {
  if (!warrantyEndDate) return null;
  const end = new Date(warrantyEndDate);
  const daysLeft = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return <Badge tone="red">Expired</Badge>;
  if (daysLeft <= WARRANTY_WARNING_DAYS) return <Badge tone="amber">Expires soon</Badge>;
  return null;
}

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const { showToast } = useToast();
  const [action, setAction] = useState<AssetActionKind | null>(null);
  const [printing, setPrinting] = useState(false);

  const assetQuery = useQuery({ queryKey: ['asset', assetId], queryFn: () => getAsset(assetId!), enabled: !!assetId });

  async function handlePrintLabel() {
    if (!assetQuery.data) return;
    setPrinting(true);
    try {
      await printAssetLabel(assetQuery.data);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not print the label.', 'error');
    } finally {
      setPrinting(false);
    }
  }

  if (!assetId) return null;

  if (assetQuery.isLoading || !assetQuery.data) {
    return (
      <div className="py-14 text-center">
        <Spinner />
      </div>
    );
  }

  const asset = assetQuery.data;
  const isTerminal = asset.status === 'DECOMMISSIONED';
  // On loan - the register shouldn't be edited around a loan in flight;
  // check it in via Loans first (LoansService.checkinLoan flips this back
  // to SPARE), otherwise the register and the loan record would disagree.
  const isLoaned = asset.status === 'LOANED';

  return (
    <div>
      <PageHeader
        breadcrumb={
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Link to="/assets" className="hover:text-slate-600">
              Asset Register
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{asset.orderLine?.customerCatalogItem?.name ?? 'Asset'}</span>
          </div>
        }
        title={asset.orderLine?.customerCatalogItem?.name ?? 'Asset'}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" isLoading={printing} onClick={handlePrintLabel}>
              <QrCode className="h-3.5 w-3.5" /> Print label
            </Button>
            <StatusBadge status={asset.status} kind="asset" />
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <span className="text-sm font-medium text-slate-600">Details</span>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Serial number" value={asset.serialNumber} />
            <Field label="Condition" value={asset.condition} />
            <Field label="Site" value={asset.currentSite?.name} />
            <Field
              label="Assigned to"
              value={
                asset.currentUser
                  ? [asset.currentUser.firstName, asset.currentUser.lastName].filter(Boolean).join(' ') ||
                    asset.currentUser.email
                  : 'Unassigned'
              }
            />
            <Field
              label="Warranty end"
              value={
                asset.warrantyEndDate ? (
                  <span className="flex items-center gap-2">
                    {new Date(asset.warrantyEndDate).toLocaleDateString()}
                    {warrantyBadge(asset.warrantyEndDate)}
                  </span>
                ) : null
              }
            />
            <Field label="Install date" value={asset.installDate ? new Date(asset.installDate).toLocaleDateString() : null} />
            <Field label="Purchase date" value={asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : null} />
            <Field label="Purchase price" value={asset.purchasePrice ? money(asset.purchasePrice) : null} />
            <Field label="Term" value={asset.termMonths ? `${asset.termMonths} months` : null} />
          </CardBody>
          <div className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-3">
            <Button size="sm" variant="secondary" disabled={isTerminal || isLoaned} onClick={() => setAction('condition')}>
              Update condition
            </Button>
            <Button size="sm" variant="secondary" disabled={isTerminal || isLoaned} onClick={() => setAction('reassign')}>
              Reassign
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={asset.status === 'RETURNED' || isTerminal || isLoaned}
              onClick={() => setAction('return')}
            >
              Return
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAction('service')}>
              <Wrench className="h-3.5 w-3.5" /> Log service
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAction('details')}>
              <Pencil className="h-3.5 w-3.5" /> Edit details
            </Button>
            <Button size="sm" variant="danger" disabled={isTerminal || isLoaned} onClick={() => setAction('decommission')}>
              Decommission
            </Button>
          </div>
          {isLoaned && (
            <p className="border-t border-slate-100 px-5 py-2 text-xs text-slate-500">
              This asset is out on loan.{' '}
              <Link to="/loans" className="text-brand-700 hover:underline">
                Check it in from Loans
              </Link>{' '}
              before making other changes.
            </p>
          )}
        </Card>

        <Card>
          <CardHeader>
            <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <History className="h-4 w-4" /> History
            </span>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            {!asset.events || asset.events.length === 0 ? (
              <p className="text-sm text-slate-400">No events yet.</p>
            ) : (
              asset.events.map((event) => (
                <div key={event.id} className="flex items-start justify-between gap-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-800">{EVENT_LABELS[event.type] ?? event.type}</span>
                    {event.notes && <span className="ml-2 text-slate-500">{event.notes}</span>}
                    {event.type === 'SERVICE' && (event.cost || event.vendor) && (
                      <span className="ml-2 text-slate-400">
                        {[event.cost ? money(event.cost) : null, event.vendor].filter(Boolean).join(' - ')}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-slate-400">{new Date(event.occurredAt).toLocaleString()}</span>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>

      {action && <AssetActionModal asset={asset} kind={action} open={!!action} onClose={() => setAction(null)} />}
    </div>
  );
}
